import {
  Activity,
  Apple,
  Baby,
  Droplets,
  HeartPulse,
  Microscope,
  Moon,
  Pill,
  Scissors,
  SearchCheck,
  ShieldAlert,
  ShieldCheck,
  Sprout,
  Syringe,
  TestTubes,
  TreeDeciduous,
  UserRound,
  Wind,
  Wine,
  type LucideIcon,
} from "lucide-react-native";
import type { Patient } from "@src/packages/patients/patients.types";
import { calculateBasicInfoCompletion } from "@louise/medical-history/basic-info/calculateBasicInfoCompletion";
import { calculateInfertilityCompletion } from "@src/packages/medical-history/fertility/infertility/calculateInfertilityCompletion";
import { calculateFertilityTestingCompletion } from "@src/packages/medical-history/fertility/fertility-testing/calculateFertilityTestingCompletion";
import { calculateFertilityTreatmentsCompletion } from "@src/packages/medical-history/fertility/fertility-treatments/calculateFertilityTreatmentsCompletion";
import { calculateMedicationsVaccinesCompletion } from "@src/packages/medical-history/medical/medications-vaccines/calculateMedicationsVaccinesCompletion";
import { calculateAllergiesCompletion } from "@src/packages/medical-history/medical/allergies/calculateAllergiesCompletion";
import { calculateSurgeriesCompletion } from "@src/packages/medical-history/medical/surgeries/calculateSurgeriesCompletion";
import { calculateDietSupplementsCompletion } from "@src/packages/medical-history/lifestyle/diet-supplements/calculateDietSupplementsCompletion";
import { calculateExposureCompletion } from "@louise/medical-history/lifestyle/exposure/calculateExposureCompletion";
import { calculateSubstancesCompletion } from "@louise/medical-history/lifestyle/substances/calculateSubstancesCompletion";
import { calculateSleepActivityCompletion } from "@louise/medical-history/lifestyle/sleep-activity/calculateSleepActivityCompletion";
import { calculateMenstrualHealthCompletion } from "@src/packages/medical-history/gynae/menstrual-health/calculateMenstrualHealthCompletion";
import { calculateDiseaseHistoryCompletion } from "@src/packages/medical-history/medical/disease-history/calculateDiseaseHistoryCompletion";
import { calculateSexualHealthCompletion } from "@src/packages/medical-history/gynae/sexual-health/calculateSexualHealthCompletion";
import { calculateContraceptionCompletion } from "@src/packages/medical-history/gynae/contraception/calculateContraceptionCompletion";
import { calculatePregnancyCompletion } from "@src/packages/medical-history/gynae/pregnancies/calculatePregnancyCompletion";
import { calculateFamilyHistoryCompletion } from "@src/packages/medical-history/family-history/calculateFamilyHistoryCompletion";

export const MEDICAL_HISTORY_XP_PER_SECTION = 50;

export type MedicalHistoryCompletion = {
  percent: number;
  label: string;
  completed: number;
  total: number;
};

export type MedicalHistorySectionKey =
  | "basicInfo"
  | "infertility"
  | "fertilityTesting"
  | "fertilityTreatments"
  | "menstrualHealth"
  | "sexualHealth"
  | "contraception"
  | "pregnancy"
  | "medicationsVaccines"
  | "allergies"
  | "diseaseHistory"
  | "surgeries"
  | "exposure"
  | "substances"
  | "sleepActivity"
  | "dietSupplements"
  | "familyHistory";

export type MedicalHistoryReviewSectionKey =
  | "basic-info"
  | "infertility"
  | "fertility-testing"
  | "fertility-treatments"
  | "menstrual-health"
  | "sexual-health"
  | "contraception"
  | "pregnancies"
  | "medications-vaccines"
  | "allergies"
  | "diseases"
  | "surgeries"
  | "exposure"
  | "substances"
  | "sleep-activity"
  | "diet-supplements"
  | "family-history";

export type MedicalHistoryFlowRoute =
  | "BasicInfoFlow"
  | "InfertilityFlow"
  | "FertilityTestingFlow"
  | "FertilityTreatmentsFlow"
  | "MenstrualHealthFlow"
  | "SexualHealthFlow"
  | "ContraceptionFlow"
  | "PregnanciesFlow"
  | "MedicationsVaccinesFlow"
  | "AllergiesFlow"
  | "DiseaseHistoryFlow"
  | "SurgeriesFlow"
  | "ExposureFlow"
  | "SubstancesFlow"
  | "SleepActivityFlow"
  | "DietSupplementsFlow"
  | "FamilyHistoryFlow";

export type MedicalHistoryGroupKey =
  | "basic"
  | "fertility"
  | "gynae"
  | "medical"
  | "lifestyle"
  | "family-history";

export type MedicalHistorySection = {
  key: MedicalHistorySectionKey;
  reviewKey: MedicalHistoryReviewSectionKey;
  route: MedicalHistoryFlowRoute;
  groupKey: MedicalHistoryGroupKey;
  titleKey: string;
  icon: LucideIcon;
  afabOnly?: boolean;
  calculateCompletion: (
    patient: Patient | null | undefined,
  ) => MedicalHistoryCompletion;
};

export type MedicalHistoryGroup = {
  key: MedicalHistoryGroupKey;
  titleKey: string;
  amabTitleKey?: string;
  icon: LucideIcon;
  amabIcon?: LucideIcon;
};

export const medicalHistoryGroups: readonly MedicalHistoryGroup[] = [
  {
    key: "basic",
    titleKey: "med_history.overview.group.basic",
    icon: UserRound,
  },
  {
    key: "fertility",
    titleKey: "med_history.overview.group.fertility",
    icon: Microscope,
  },
  {
    key: "gynae",
    titleKey: "med_history.overview.group.gynae",
    amabTitleKey: "med_history.overview.group.gynae_male",
    icon: Baby,
    amabIcon: HeartPulse,
  },
  {
    key: "medical",
    titleKey: "med_history.overview.group.medical",
    icon: HeartPulse,
  },
  {
    key: "lifestyle",
    titleKey: "med_history.overview.group.lifestyle",
    icon: Sprout,
  },
  {
    key: "family-history",
    titleKey: "med_history.overview.group.family_history",
    icon: TreeDeciduous,
  },
] as const;

export const medicalHistorySections: readonly MedicalHistorySection[] = [
  {
    key: "basicInfo",
    reviewKey: "basic-info",
    route: "BasicInfoFlow",
    groupKey: "basic",
    titleKey: "med_history.overview.row.basic_info",
    icon: UserRound,
    calculateCompletion: calculateBasicInfoCompletion,
  },
  {
    key: "infertility",
    reviewKey: "infertility",
    route: "InfertilityFlow",
    groupKey: "fertility",
    titleKey: "med_history.overview.row.infertility",
    icon: SearchCheck,
    calculateCompletion: calculateInfertilityCompletion,
  },
  {
    key: "fertilityTesting",
    reviewKey: "fertility-testing",
    route: "FertilityTestingFlow",
    groupKey: "fertility",
    titleKey: "med_history.overview.row.fertility_testing",
    icon: TestTubes,
    calculateCompletion: calculateFertilityTestingCompletion,
  },
  {
    key: "fertilityTreatments",
    reviewKey: "fertility-treatments",
    route: "FertilityTreatmentsFlow",
    groupKey: "fertility",
    titleKey: "med_history.overview.row.fertility_treatments",
    icon: Syringe,
    calculateCompletion: calculateFertilityTreatmentsCompletion,
  },
  {
    key: "menstrualHealth",
    reviewKey: "menstrual-health",
    route: "MenstrualHealthFlow",
    groupKey: "gynae",
    titleKey: "med_history.overview.row.menstrual_health",
    icon: Droplets,
    afabOnly: true,
    calculateCompletion: calculateMenstrualHealthCompletion,
  },
  {
    key: "sexualHealth",
    reviewKey: "sexual-health",
    route: "SexualHealthFlow",
    groupKey: "gynae",
    titleKey: "med_history.overview.row.sexual_health",
    icon: HeartPulse,
    calculateCompletion: calculateSexualHealthCompletion,
  },
  {
    key: "contraception",
    reviewKey: "contraception",
    route: "ContraceptionFlow",
    groupKey: "gynae",
    titleKey: "med_history.overview.row.contraception",
    icon: ShieldCheck,
    calculateCompletion: calculateContraceptionCompletion,
  },
  {
    key: "pregnancy",
    reviewKey: "pregnancies",
    route: "PregnanciesFlow",
    groupKey: "gynae",
    titleKey: "med_history.overview.row.pregnancies",
    icon: Baby,
    afabOnly: true,
    calculateCompletion: calculatePregnancyCompletion,
  },
  {
    key: "medicationsVaccines",
    reviewKey: "medications-vaccines",
    route: "MedicationsVaccinesFlow",
    groupKey: "medical",
    titleKey: "med_history.overview.row.medications_vaccines",
    icon: Pill,
    calculateCompletion: calculateMedicationsVaccinesCompletion,
  },
  {
    key: "allergies",
    reviewKey: "allergies",
    route: "AllergiesFlow",
    groupKey: "medical",
    titleKey: "med_history.overview.row.allergies",
    icon: ShieldAlert,
    calculateCompletion: calculateAllergiesCompletion,
  },
  {
    key: "diseaseHistory",
    reviewKey: "diseases",
    route: "DiseaseHistoryFlow",
    groupKey: "medical",
    titleKey: "med_history.overview.row.diseases",
    icon: Activity,
    calculateCompletion: calculateDiseaseHistoryCompletion,
  },
  {
    key: "surgeries",
    reviewKey: "surgeries",
    route: "SurgeriesFlow",
    groupKey: "medical",
    titleKey: "med_history.overview.row.surgeries",
    icon: Scissors,
    calculateCompletion: calculateSurgeriesCompletion,
  },
  {
    key: "exposure",
    reviewKey: "exposure",
    route: "ExposureFlow",
    groupKey: "lifestyle",
    titleKey: "med_history.overview.row.exposure",
    icon: Wind,
    calculateCompletion: calculateExposureCompletion,
  },
  {
    key: "substances",
    reviewKey: "substances",
    route: "SubstancesFlow",
    groupKey: "lifestyle",
    titleKey: "med_history.overview.row.substances",
    icon: Wine,
    calculateCompletion: calculateSubstancesCompletion,
  },
  {
    key: "sleepActivity",
    reviewKey: "sleep-activity",
    route: "SleepActivityFlow",
    groupKey: "lifestyle",
    titleKey: "med_history.overview.row.sleep_activity",
    icon: Moon,
    calculateCompletion: calculateSleepActivityCompletion,
  },
  {
    key: "dietSupplements",
    reviewKey: "diet-supplements",
    route: "DietSupplementsFlow",
    groupKey: "lifestyle",
    titleKey: "med_history.overview.row.diet_supplements",
    icon: Apple,
    calculateCompletion: calculateDietSupplementsCompletion,
  },
  {
    key: "familyHistory",
    reviewKey: "family-history",
    route: "FamilyHistoryFlow",
    groupKey: "family-history",
    titleKey: "med_history.overview.row.family_history",
    icon: TreeDeciduous,
    calculateCompletion: calculateFamilyHistoryCompletion,
  },
] as const;

export type MedicalHistoryCompletionMap = Record<
  MedicalHistorySectionKey,
  MedicalHistoryCompletion
>;

export type VisibleMedicalHistoryGroup = MedicalHistoryGroup & {
  titleKey: string;
  icon: LucideIcon;
  sections: MedicalHistorySection[];
};

export function shouldHideAfabOnlyMedicalHistorySections(
  patient: Patient | null | undefined,
) {
  return patient?.gender === "AMAB";
}

export function getVisibleMedicalHistorySections(
  patient: Patient | null | undefined,
) {
  const hideAfabOnly = shouldHideAfabOnlyMedicalHistorySections(patient);
  return medicalHistorySections.filter(
    (section) => !(hideAfabOnly && section.afabOnly),
  );
}

export function getMedicalHistoryCompletionMap(
  patient: Patient | null | undefined,
): MedicalHistoryCompletionMap {
  return medicalHistorySections.reduce((acc, section) => {
    acc[section.key] = section.calculateCompletion(patient);
    return acc;
  }, {} as MedicalHistoryCompletionMap);
}

export function getVisibleMedicalHistoryGroups(
  patient: Patient | null | undefined,
): VisibleMedicalHistoryGroup[] {
  const visibleSections = getVisibleMedicalHistorySections(patient);
  const hideAfabOnly = shouldHideAfabOnlyMedicalHistorySections(patient);

  return medicalHistoryGroups
    .map((group) => {
      const sections = visibleSections.filter(
        (section) => section.groupKey === group.key,
      );
      return {
        ...group,
        titleKey:
          hideAfabOnly && group.amabTitleKey
            ? group.amabTitleKey
            : group.titleKey,
        icon: hideAfabOnly && group.amabIcon ? group.amabIcon : group.icon,
        sections,
      };
    })
    .filter((group) => group.sections.length > 0);
}

export function getMedicalHistoryProgressSummary(
  patient: Patient | null | undefined,
) {
  const completionData = getMedicalHistoryCompletionMap(patient);
  const visibleSections = getVisibleMedicalHistorySections(patient);
  const completedSections = visibleSections.filter(
    (section) => completionData[section.key].percent >= 0.999,
  ).length;
  const totalSections = visibleSections.length;

  return {
    completionData,
    completedSections,
    totalSections,
    progressPercent:
      totalSections > 0
        ? Math.round((completedSections / totalSections) * 100)
        : 0,
    xp: completedSections * MEDICAL_HISTORY_XP_PER_SECTION,
  };
}

export function getNextMedicalHistorySection(
  currentSectionKey: string,
  patient?: Patient | null,
) {
  const visibleSections = getVisibleMedicalHistorySections(patient);
  const currentVisibleIndex = visibleSections.findIndex(
    (section) => section.key === currentSectionKey,
  );

  if (currentVisibleIndex >= 0) {
    return visibleSections[currentVisibleIndex + 1] ?? null;
  }

  const currentIndex = medicalHistorySections.findIndex(
    (section) => section.key === currentSectionKey,
  );
  if (currentIndex < 0) return null;

  return (
    visibleSections.find((section) => {
      const fullIndex = medicalHistorySections.findIndex(
        (candidate) => candidate.key === section.key,
      );
      return fullIndex > currentIndex;
    }) ?? null
  );
}
